import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Tabs as MuiTabs, useMediaQuery, useTheme } from '@mui/material'
import Tab from '@mui/material/Tab'
import { alpha, styled } from '@mui/material/styles'
import { Container } from 'components/ui'
import styles from './Tabs.module.scss'
import DropdownMenu from 'components/form/DropdownMenu'
import colors from 'theme/colors'

export type TabData = {
  label: React.ReactNode | string
  badgeColor?: string
  badgeContent?: string
  badgeTextColor?: string
  filterValue?: string
  id?: string
}

export type TabDataLabel = TabData & {
  formatLabel?: string
}

type MuiTabProps = {
  label: React.ReactNode | string
  value: number | string
  key: string
  iconPosition?: 'top' | 'start' | 'end' | 'bottom'
  icon?: any
  id?: string
}

const MuiTab = styled((props: MuiTabProps) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  fontWeight: 500,
  width: 'auto',
  minHeight: '45px',
  position: 'relative',
  borderRadius:
    typeof theme.shape.borderRadius === 'number'
      ? theme.shape.borderRadius * 2
      : `calc(${theme.shape.borderRadius} * 2)`,
  overflow: 'visible',
  transition: theme.transitions.create(['color', 'background-color', 'transform'], {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeOut,
  }),
  [theme.breakpoints.up('sm')]: {
    minWidth: 0,
  },
  marginRight: theme.spacing(1),
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0) scale(0.98)',
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    transform: 'translateY(-1px)',
  },
  '&:focus-visible': {
    outline: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    outlineOffset: 2,
  },
}))

type TabsProps = {
  tabData: TabData[]
  value: number
  iconPosition?: 'top' | 'start' | 'end' | 'bottom'
  containerClass?: string
  containerSx?: any
  handleChange: (event: React.SyntheticEvent, newValue: number) => void
  allowUniqueTab?: boolean
  forceVisibleUntilId?: string
  hideAfterForceVisible?: boolean
}

const Tabs = (props: TabsProps) => {
  const {
    tabData,
    value,
    handleChange,
    iconPosition = 'end',
    containerClass,
    containerSx,
    allowUniqueTab = false,
    forceVisibleUntilId,
    hideAfterForceVisible = false,
  } = props

  const theme = useTheme()
  const isSM = useMediaQuery(theme.breakpoints.down(835))
  const isMD = useMediaQuery(theme.breakpoints.down(1200))
  const isLG = useMediaQuery(theme.breakpoints.down(1620))

  const tabMotionSx = useMemo(
    () => ({
      width: '100%',
      '& .MuiTabs-flexContainer': {
        gap: theme.spacing(0.5),
      },
      '& .MuiTabs-indicator': {
        display: 'none',
      },
    }),
    [theme]
  )

  const [visibleTabs, setVisibleTabs] = useState<TabData[]>([])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const tabsRef = useRef<HTMLDivElement | null>(null)
  const drawerRef = useRef<HTMLElement | null>(null)
  const containerClassName = ['fc-tabs', containerClass].filter(Boolean).join(' ')

  const forcedVisibleCount = useMemo(() => {
    if (!forceVisibleUntilId) return 0
    const idx = tabData.findIndex((tab) => tab.id === forceVisibleUntilId)
    return idx >= 0 ? idx + 1 : 0
  }, [forceVisibleUntilId, tabData])

  const resolveDrawerElement = useCallback(() => {
    if (typeof document === 'undefined') return null
    if (drawerRef.current && document.body.contains(drawerRef.current)) return drawerRef.current
    const drawerEl =
      (typeof document !== 'undefined'
        ? (document.querySelector('.fc-menu-bar .MuiDrawer-paper') as HTMLElement | null) ||
          (document.querySelector('.fc-menu-bar .MuiDrawer-root') as HTMLElement | null)
        : null) || null
    drawerRef.current = drawerEl
    return drawerEl
  }, [])

  const getFallbackDrawerWidth = useCallback(() => {
    const storedState = typeof localStorage !== 'undefined' ? localStorage.getItem('menuBarOpen') : null
    const isOpen = storedState ? Boolean(JSON.parse(storedState)) : true
    const closedWidth = Number(String(theme.spacing(10)).replace('px', '')) || 80
    return isOpen ? 256 : closedWidth + 1
  }, [theme])

  const getDrawerWidth = useCallback(() => {
    const drawerEl = resolveDrawerElement()
    if (drawerEl) return drawerEl.getBoundingClientRect().width
    return getFallbackDrawerWidth()
  }, [getFallbackDrawerWidth, resolveDrawerElement])

  const syncVisibleTabs = useCallback(() => {
    if (!tabData.length) {
      setVisibleTabs([])
      setIsDropdownOpen(false)
      return
    }

    const containerWidth = tabsRef.current?.offsetWidth ?? 0
    const drawerWidth = getDrawerWidth()
    const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0
    const fallbackContainerWidth = Math.max(0, windowWidth - drawerWidth - 48)
    const availableWidth = containerWidth > 0 ? containerWidth : fallbackContainerWidth
    const fallbackVisibleCount = isSM ? 2 : isMD ? 4 : isLG ? 6 : Math.min(12, tabData.length)
    const dropdownSpace = tabData.length > fallbackVisibleCount ? 72 : 56
    const tabMinWidth = isSM ? 120 : 150

    const fitByWidth =
      availableWidth > 0 ? Math.floor((availableWidth - dropdownSpace) / tabMinWidth) : fallbackVisibleCount

    let nextVisibleCount = Math.max(1, Math.min(tabData.length, fitByWidth || fallbackVisibleCount))

    if (forcedVisibleCount) {
      nextVisibleCount = Math.max(nextVisibleCount, forcedVisibleCount)
      if (hideAfterForceVisible) {
        nextVisibleCount = Math.min(nextVisibleCount, forcedVisibleCount)
      }
    }

    const nextVisibleTabs = tabData.slice(0, nextVisibleCount)
    const hasHiddenTabs = tabData.length > nextVisibleCount

    setVisibleTabs((prevTabs) => {
      const sameLength = prevTabs.length === nextVisibleTabs.length
      const sameOrder = sameLength && prevTabs.every((tab, index) => tab === nextVisibleTabs[index])
      return sameOrder ? prevTabs : nextVisibleTabs
    })
    setIsDropdownOpen((prev) => (prev === hasHiddenTabs ? prev : hasHiddenTabs))
  }, [forcedVisibleCount, getDrawerWidth, hideAfterForceVisible, isLG, isMD, isSM, tabData])

  useEffect(() => {
    syncVisibleTabs()
  }, [syncVisibleTabs])

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncVisibleTabs)
      return () => window.removeEventListener('resize', syncVisibleTabs)
    }

    const observer = new ResizeObserver(() => syncVisibleTabs())
    const target = tabsRef.current
    if (target) observer.observe(target)

    return () => observer.disconnect()
  }, [syncVisibleTabs])

  useEffect(() => {
    const drawerEl = resolveDrawerElement()
    if (!drawerEl || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => syncVisibleTabs())
    observer.observe(drawerEl)

    return () => observer.disconnect()
  }, [resolveDrawerElement, syncVisibleTabs])

  const hiddenTabs = useMemo(() => tabData.slice(visibleTabs.length), [tabData, visibleTabs.length])

  const handleDropdownChange = (_event: React.SyntheticEvent | null, newValue: number) => {
    handleChange(_event || ({} as React.SyntheticEvent), newValue)
  }

  if (!allowUniqueTab && visibleTabs?.length <= 1) return <></>

  return (
    <Container sx={{ position: 'relative', ...containerSx }} className={containerClassName}>
      <div ref={tabsRef} style={{ width: '100%', minWidth: 0 }}>
        <MuiTabs
          value={value}
          onChange={(e, val) => handleChange(e, val)}
          orientation={isSM ? 'vertical' : 'horizontal'}
          sx={tabMotionSx}
        >
          {visibleTabs.map((item, index) => (
            <MuiTab
              key={`key-${index}`}
              icon={
                item.badgeContent && (
                  <Container
                    className={styles.badge}
                    sx={{ background: item.badgeColor || colors.grey10, color: item.badgeTextColor || colors.white }}
                  >
                    {item.badgeContent}
                  </Container>
                )
              }
              value={index}
              label={item.label}
              iconPosition={iconPosition}
              id={item.id}
            />
          ))}
          {isDropdownOpen && hiddenTabs.length > 0 && (
            <DropdownMenu
              dropdownOptions={hiddenTabs}
              numberOfVisibleTabs={visibleTabs.length}
              onChange={handleDropdownChange}
            />
          )}
        </MuiTabs>
      </div>
    </Container>
  )
}

export default Tabs
